
import { defineComponent, computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { isStandalonePwaMode } from '@/services/spaBuildMeta'
import { isSpaStaleUiPreviewDev } from '@/services/spaStalePreview'

const UPDATE_LOCK_ILLU_URL = '/update-blocking-illustration.jpg?v=10'

type UpdateStep = 'sw' | 'push' | 'reload'
type PreviewStep = UpdateStep | 'done'

export default defineComponent({
  name: 'CrmSpaStaleUpdateBanner',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['snooze'],
  setup(props, { emit }) {
    const lockImageVisible = ref(false)
    const isUpdating = ref(false)
    const previewLock = ref(false)
    const updateStep = ref<UpdateStep>('sw')
    const previewStep = ref<PreviewStep>('sw')
    const updateError = ref<string | null>(null)
    const animatedProgress = ref(0)
    let previewTimers: number[] = []
    let progressRaf: number | null = null

    const staleHint = computed(() =>
      isStandalonePwaMode()
        ? 'Установленное приложение обновится после перезагрузки — удобнее всего нажать «Обновить».'
        : 'Можно потянуть страницу вниз от верха экрана или нажать «Обновить».',
    )

    const updateStepText = computed(() => {
      if (previewLock.value) {
        if (previewStep.value === 'sw') {
          return 'Проверяем наличие новой версии приложения и подготавливаем обновление.'
        }
        if (previewStep.value === 'push') {
          return 'Синхронизируем важные параметры и уведомления для текущего устройства.'
        }
        if (previewStep.value === 'reload') {
          return 'Применяем обновление и готовим перезапуск страницы.'
        }
        return 'Перезапускаем страницу...'
      }
      if (updateError.value) {
        return 'Не удалось завершить обновление автоматически.'
      }
      if (updateStep.value === 'sw') {
        return 'Проверяем и применяем новую версию...'
      }
      if (updateStep.value === 'push') {
        return 'Синхронизируем важные параметры...'
      }
      return 'Перезапускаем страницу...'
    })

    function targetProgressForStep(step: PreviewStep | UpdateStep, isPreview: boolean): number {
      if (isPreview) {
        if (step === 'sw') return 28
        if (step === 'push') return 62
        if (step === 'reload') return 88
        return 100
      }
      if (step === 'sw') return 30
      if (step === 'push') return 70
      return 95
    }

    function animateProgressTo(target: number, durationMs = 700): void {
      if (progressRaf != null) {
        cancelAnimationFrame(progressRaf)
        progressRaf = null
      }
      const from = animatedProgress.value
      if (from === target) {
        return
      }
      const started = performance.now()
      const tick = (now: number) => {
        const t = Math.min(1, (now - started) / durationMs)
        const eased = 1 - (1 - t) * (1 - t)
        animatedProgress.value = Math.round(from + (target - from) * eased)
        if (t < 1) {
          progressRaf = requestAnimationFrame(tick)
        } else {
          progressRaf = null
          animatedProgress.value = target
        }
      }
      progressRaf = requestAnimationFrame(tick)
    }

    const progressPercent = computed(() => animatedProgress.value)

    const effectiveStep = computed<UpdateStep>(() => {
      if (!previewLock.value) {
        return updateStep.value
      }
      return previewStep.value === 'done' ? 'reload' : previewStep.value
    })

    const stepRows = computed(() => {
      const current = effectiveStep.value
      const done =
        previewLock.value && previewStep.value === 'done'
          ? { sw: true, push: true, reload: true }
          : {
              sw: current !== 'sw',
              push: current === 'reload',
              reload: false,
            }
      return [
        {
          key: 'sw',
          title: 'Проверка новой версии',
          hint: 'Service Worker и кэш приложения',
          active: current === 'sw' && !(previewLock.value && previewStep.value === 'done'),
          done: done.sw,
        },
        {
          key: 'push',
          title: 'Синхронизация параметров',
          hint: 'Push и пользовательские настройки',
          active: current === 'push',
          done: done.push,
        },
        {
          key: 'reload',
          title: 'Применение обновления',
          hint: 'Перезапуск страницы с новой сборкой',
          active: current === 'reload' && !(previewLock.value && previewStep.value === 'done'),
          done: done.reload,
        },
      ]
    })

    function emitSnooze() {
      emit('snooze')
    }

    function clearPreviewTimers() {
      previewTimers.forEach((id) => window.clearTimeout(id))
      previewTimers = []
    }

    function openPreviewLock() {
      clearPreviewTimers()
      if (progressRaf != null) {
        cancelAnimationFrame(progressRaf)
        progressRaf = null
      }
      previewLock.value = true
      previewStep.value = 'sw'
      updateError.value = null
      updateStep.value = 'sw'
      animatedProgress.value = 8
      animateProgressTo(28, 900)

      previewTimers.push(
        window.setTimeout(() => {
          previewStep.value = 'push'
          animateProgressTo(62, 800)
        }, 1400),
      )
      previewTimers.push(
        window.setTimeout(() => {
          previewStep.value = 'reload'
          animateProgressTo(88, 800)
        }, 2900),
      )
      previewTimers.push(
        window.setTimeout(() => {
          previewStep.value = 'done'
          animateProgressTo(100, 600)
        }, 4400),
      )
    }

    function closePreviewLock() {
      previewLock.value = false
      clearPreviewTimers()
      if (progressRaf != null) {
        cancelAnimationFrame(progressRaf)
        progressRaf = null
      }
    }

    function onPreviewEvent() {
      openPreviewLock()
    }

    /** В DEV-превью (?crm_stale_preview=1) — демо без реального SW/reload. */
    function onUpdateClick() {
      if (isSpaStaleUiPreviewDev()) {
        openPreviewLock()
        return
      }
      void reloadApp()
    }

    function forceReloadNow() {
      const url = new URL(window.location.href)
      url.searchParams.set('crm_reload_ts', String(Date.now()))
      window.location.replace(url.toString())
    }

    async function reloadApp() {
      if (isUpdating.value) {
        return
      }
      isUpdating.value = true
      updateError.value = null
      animatedProgress.value = 8
      try {
        updateStep.value = 'sw'
        animateProgressTo(30, 500)
        try {
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations()
            await Promise.all(regs.map((reg) => reg.update().catch(() => undefined)))
            await navigator.serviceWorker.ready.catch(() => undefined)
          }
        } catch {
          /* ignore */
        }

        updateStep.value = 'push'
        animateProgressTo(70, 500)
        try {
          const { syncPushTokenOnSpaUpdate } = await import('@/services/spaUpdatePushSync')
          await syncPushTokenOnSpaUpdate()
        } catch {
          /* нет входа / нет разрешения — перезагрузка всё равно нужна */
        }

        updateStep.value = 'reload'
        animateProgressTo(95, 400)
        forceReloadNow()
      } catch {
        updateError.value = 'Проблема при обновлении. Попробуйте ещё раз или перезагрузите страницу вручную.'
        isUpdating.value = false
      }
    }

    function warmUpdateLockIllustration(): void {
      if (typeof window === 'undefined') {
        return
      }
      const im = new Image()
      im.src = UPDATE_LOCK_ILLU_URL
    }

    watch(
      () => props.value,
      (v) => {
        if (v) {
          warmUpdateLockIllustration()
        }
      },
      { immediate: true },
    )

    watch([isUpdating, previewLock], (vals) => {
      const [updating, preview] = vals as [boolean, boolean]
      if (updating || preview) {
        // Сброс → показ: у кэшированных картинок @load может не сработать повторно
        lockImageVisible.value = false
        window.setTimeout(() => {
          lockImageVisible.value = true
        }, 40)
      }
    })

    watch(updateStep, (step) => {
      if (!previewLock.value && isUpdating.value) {
        animateProgressTo(targetProgressForStep(step, false))
      }
    })

    onMounted(() => {
      window.addEventListener('crm-preview-stale-update-lock', onPreviewEvent)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('crm-preview-stale-update-lock', onPreviewEvent)
      clearPreviewTimers()
      if (progressRaf != null) {
        cancelAnimationFrame(progressRaf)
        progressRaf = null
      }
    })

    return {
      UPDATE_LOCK_ILLU_URL,
      lockImageVisible,
      isUpdating,
      previewLock,
      updateError,
      staleHint,
      updateStepText,
      progressPercent,
      stepRows,
      emitSnooze,
      onUpdateClick,
      reloadApp,
      forceReloadNow,
      closePreviewLock,
    }
  },
})
